import React, { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import { CmsContext } from 'logic/context/cms';
import { FAQType } from './FAQType';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { colors } from 'src/styles/colors';
import { FAQList } from './FAQList';
import { translate, translationKeys } from 'logic/translations/translations.service';

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const FAQWrapper = styled.div<{ min1024: boolean, minTablet: boolean, maxMobileLarge: boolean }>`
  && {
    width: ${({ min1024, maxMobileLarge }) => (min1024 ? '' : maxMobileLarge ? '100%' : '80%')};
    margin: ${({ minTablet, maxMobileLarge }) => (minTablet ? '60px auto 0 auto' : maxMobileLarge ? '32px 0 0 0' : '32px auto 0 auto')};
    display: flex;
    flex-wrap: wrap;
    user-select: none;
    & .ant-collapse {
      margin: 0 auto 16px auto;
      width: ${({ min1024 }) => (min1024 ? '930px' : '100%')};
      & .ant-collapse-arrow {
        right: ${({ minTablet }) => (minTablet ? '32px' : '18px')};
        & svg {
          width: 16px;
          height: 11px;
        }
      }
      &-header {
        color: ${colors.black};
        font-size: 18px;
        font-weight: 600;
        padding: ${({ minTablet }) => (minTablet ? '32px 58px 32px 24px' : '16px 50px 16px 10px')};
      }
      &-content-box {
        padding: ${({ minTablet }) => (minTablet ? '0 24px 32px 24px' : '16px 10px')} !important;
        font-size: 14px;
      }
      &-item {
        background-color: ${colors.white};
        border: 1px solid ${colors.gray15};
        border-radius: 10px;
        &-active {
          & .ant-collapse-header {
            color: ${colors.primary};
            padding-bottom: 15px;
          }
        }
      }
    }
  }
`;

type Type = {
  icon: string;
  type: string;
  items: {
    content: { content: string };
    quoteAuthor: string;
  }[];
  eliteItems?: {
    content: { content: string };
    quoteAuthor: string;
  }[];
};

const SubTypeWrapper = styled.div<any>`
  width: ${({ minTablet }) => (minTablet ? '60%' : '100%')};
  margin-top: 60px;
  background-color: ${colors.primary};
  border-radius: 5px;
  display: flex;
`;

const SubTypeItem = styled.div<any>`
  margin: 5px;
  padding: 10px 0 10px 0;
  background-color: ${({ subTypeSwitch }) => (subTypeSwitch ? colors.white : colors.primary)};
  color: ${({ subTypeSwitch }) => (subTypeSwitch ? colors.primary : colors.white)};
  border-radius: 5px;
  width: 100%;
  text-align: center;
  cursor: pointer;
`;

const StyledHeading = styled.span`
  font-size: 22px;
  font-weight: 600;
  margin: 0 !important;
`;

export const FAQTypes: FC = () => {
  const { minTablet, maxMobileLarge, min1024 } = useBreakpoints();
  const [selected, updateSelected] = useState(1);
  const cms = useContext(CmsContext);
  const { faqInstitut, faqGebeNachhilfe, faqSucheNachhilfe, faqGebeNachhilfeElite, faqSucheNachhilfeElite } = cms;
  const [eliteSwitch, setEliteSwitch] = useState(false);

  const setFAQ = (idx: number) => {
    updateSelected(idx === selected ? -1 : idx);
  };

  const Types: Type[] = [
    {
      icon: '/icons/aboutus/type7.png',
      type: 'first',
      items: faqGebeNachhilfe,
      eliteItems: faqGebeNachhilfeElite,
    },
    {
      icon: '/icons/aboutus/type8.png',
      type: 'second',
      items: faqSucheNachhilfe,
      eliteItems: faqSucheNachhilfeElite,
    },
    {
      icon: '/icons/aboutus/type2.png',
      type: 'last',
      items: faqInstitut,
    },
  ];

  return (
    <>
      <Content>
        {Types.map((props, idx) => (
          <FAQType active={idx === selected} {...props} idx={idx} onClick={setFAQ} key={idx} />
        ))}
      </Content>
      {selected !== -1 && Types[selected]?.eliteItems &&
        <SubTypeWrapper minTablet={minTablet}>
          <SubTypeItem subTypeSwitch={!eliteSwitch} onClick={() => setEliteSwitch(false)}>
            <StyledHeading>{translate(translationKeys.faq.subItems.marketplace)}</StyledHeading>
          </SubTypeItem>
          <SubTypeItem subTypeSwitch={eliteSwitch} onClick={() => setEliteSwitch(true)}>
            <StyledHeading>{translate(translationKeys.faq.subItems.elite)}</StyledHeading>
          </SubTypeItem>
        </SubTypeWrapper>
      }
      <FAQWrapper minTablet={minTablet} min1024={min1024} maxMobileLarge={maxMobileLarge}>
        {selected !== -1 &&
          (!Types[selected]?.eliteItems || !eliteSwitch) ? (
          Types[selected].items.map(({ content: { content }, quoteAuthor }, idx) => (
            <FAQList
              active={selected}
              content={content}
              quoteAuthor={quoteAuthor}
              idx={idx}
              key={idx}
            />
          ))
        ) : (
          Types[selected].eliteItems?.map(({ content: { content }, quoteAuthor }, idx) => (
            <FAQList
              active={selected}
              content={content}
              quoteAuthor={quoteAuthor}
              idx={idx}
              key={idx}
            />
          ))
        )
        }
      </FAQWrapper>
    </>
  );
};
